import { type JSXElementConstructor, type ReactNode, useMemo } from 'react';

import {
  type DecorateSlotChildrenOptionsInterface,
  decorateSlotChildren,
} from '../utils/decorate-slot-children';

type DefaultElement = keyof JSX.IntrinsicElements | JSXElementConstructor<any>;

const identityTemplate = (childContent: ReactNode) => childContent;

export interface UseDecoratedSlotContentPropsInterface<
  ElementType extends DefaultElement = 'div',
> extends DecorateSlotChildrenOptionsInterface<ElementType> {
  /**
   * Optional function to wrap/transform the final content. This may be inline;
   * values captured by the template must be listed in `dependencies`.
   */
  template?: (childContent: ReactNode) => ReactNode;

  /** Values captured by `template` that should invalidate the decorated content. */
  dependencies?: Array<any>;
}

/**
 * Memoized wrapper around `decorateSlotChildren` for component templates that
 * need nested Radix Slot/asChild composition. Template identity is intentionally
 * ignored; pass values captured by `template` through `dependencies`.
 */
export function useDecoratedSlotContent<ElementType extends DefaultElement = 'div'>({
  children,
  asChild = false,
  childProps,
  template,
  alternativeContent,
  fallback,
  dependencies = [],
}: UseDecoratedSlotContentPropsInterface<ElementType>): ReactNode {
  return useMemo(
    () =>
      decorateSlotChildren(
        {
          children,
          asChild,
          childProps,
          alternativeContent,
          fallback,
        },
        template ?? identityTemplate,
      ),
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [children, asChild, childProps, alternativeContent, fallback, ...dependencies],
  );
}
